/* VSS Smart Merchandising Hero 2.0. No dependencies; no global scroll/key overrides. */
(() => {
  if (customElements.get('vss-merchandising-hero')) return;
  const parseDate = value => {
    if (!value) return null;
    const m = /^(20\d{2})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(Z|[+-]\d{2}:\d{2})$/.exec(value);
    if (!m || +m[1] < 2020 || +m[2] < 1 || +m[2] > 12 || +m[3] < 1 || +m[4] > 23 || +m[5] > 59 || +m[6] > 59) return NaN;
    const days = new Date(Date.UTC(+m[1], +m[2], 0)).getUTCDate();
    if (+m[3] > days) return NaN;
    if (m[7] !== 'Z' && (+m[7].slice(1, 3) > 14 || +m[7].slice(4) > 59 || (+m[7].slice(1, 3) === 14 && +m[7].slice(4) !== 0))) return NaN;
    return Date.parse(value);
  };
  class VSSMerchandisingHero extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      this.main = this.querySelector('[data-feature-panel]');
      this.track = this.querySelector('[data-feature-track]');
      this.rail = this.querySelector('[data-promo-panel]');
      this.promoTrack = this.querySelector('[data-promo-track]');
      this.featureControls = this.querySelector('[data-feature-controls]');
      this.railControls = this.querySelector('[data-rail-controls]');
      this.dots = this.querySelector('[data-dots]');
      this.rotation = this.querySelector('[data-rotation]');
      this.status = this.querySelector('[data-status]');
      if (!this.track || !this.promoTrack) return;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.editor = this.dataset.editor === 'true';
      this.userPaused = this.editor || this.motion.matches || this.dataset.autoplay !== 'true';
      this.inViewport = false;
      this.hovered = false;
      this.current = 0;
      this.featureEntries = this.readEntries(this.track);
      this.promoEntries = this.readEntries(this.promoTrack);
      this.reconcile();
      this.listen(this, 'click', e => this.click(e));
      this.listen(this.main, 'focusin', () => this.pause());
      this.listen(this.rotation, 'pointerdown', () => { this.rotationPointerState = this.userPaused; });
      this.listen(this.main, 'pointerenter', e => {
        if (e.pointerType !== 'touch') { this.hovered = true; this.syncRotation(); }
      });
      this.listen(this.main, 'pointerleave', () => { this.hovered = false; this.syncRotation(); });
      this.listen(this.main, 'keydown', e => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key) || e.altKey || e.ctrlKey || e.metaKey) return;
        e.preventDefault(); this.pause();
        const next = e.key === 'Home' ? 0 : e.key === 'End' ? this.features.length - 1 : this.current + (e.key === 'ArrowRight' ? 1 : -1);
        this.show(next, true);
      });
      this.listen(this.track, 'pointerdown', e => {
        if (e.pointerType === 'mouse' || !e.isPrimary) return;
        this.touch = { x:e.clientX, y:e.clientY, id:e.pointerId };
        this.pause();
      }, { passive:true });
      this.listen(this.track, 'pointerup', e => {
        const t = this.touch; this.touch = null;
        if (!t || t.id !== e.pointerId) return;
        const dx = e.clientX - t.x, dy = e.clientY - t.y;
        if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.4) {
          this.suppressClickUntil = performance.now() + 400;
          this.show(this.current + (dx < 0 ? 1 : -1), true);
        }
      }, { passive:true });
      this.listen(this.track, 'pointercancel', () => { this.touch = null; }, { passive:true });
      this.listen(this.track, 'click', e => {
        if (performance.now() < (this.suppressClickUntil || 0)) { e.preventDefault(); e.stopPropagation(); }
      }, { capture:true });
      this.listen(this.promoTrack, 'scroll', () => {
        cancelAnimationFrame(this.railFrame);
        this.railFrame = requestAnimationFrame(() => this.syncRail());
      }, { passive:true });
      this.listen(this.promoTrack, 'focusin', e => this.revealPromo(e.target.closest('[data-campaign]')));
      this.listen(document, 'visibilitychange', () => { this.reconcile(); this.syncRotation(); });
      this.listen(window, 'pageshow', () => this.reconcile());
      this.listen(this.motion, 'change', () => { if (this.motion.matches) this.pause(); });
      if (this.editor) {
        this.listen(document, 'shopify:block:select', e => this.editorSelect(e));
        this.listen(document, 'shopify:block:deselect', () => { this.editorSelection = null; this.reconcile(); });
      }
      this.resize = new ResizeObserver(() => this.syncRail());
      this.resize.observe(this.promoTrack);
      this.viewport = new IntersectionObserver(entries => {
        this.inViewport = entries.some(e => e.isIntersecting);
        this.syncRotation();
      }, { threshold:.1 });
      this.viewport.observe(this.main);
      this.overlays = new MutationObserver(() => this.syncRotation());
      this.overlays.observe(document.body, { attributes:true, attributeFilter:['class'] });
      const header = document.querySelector('store-header');
      if (header) this.overlays.observe(header, { attributes:true, subtree:true, attributeFilter:['class','open','aria-expanded'] });
      this.dataset.ready = 'true';
      this.syncRail();
    }
    listen(target, type, fn, options = {}) {
      target?.addEventListener(type, fn, { ...options, signal:this.events.signal });
    }
    readEntries(track) {
      return [...track.children].filter(n => n.matches('[data-campaign],template[data-campaign-template]')).map(source => {
        const template = source instanceof HTMLTemplateElement;
        const node = template ? source.content.firstElementChild : source;
        const anchor = document.createComment('VSS campaign position');
        source.after(anchor);
        if (template) source.remove();
        const start = parseDate(node.dataset.start), end = parseDate(node.dataset.end);
        return { node, anchor, start, end, valid:!Number.isNaN(start) && !Number.isNaN(end) && (start == null || end == null || start < end) };
      });
    }
    eligible(entry, now) {
      return entry.valid && (entry.start == null || now >= entry.start) && (entry.end == null || now < entry.end);
    }
    reconcile() {
      clearTimeout(this.dateTimer);
      const now = Date.now();
      const old = this.features?.[this.current];
      let restoreFocus = false;
      let nextBoundary = Infinity;
      for (const entry of [...this.featureEntries, ...this.promoEntries]) {
        const preview = this.editor && this.editorSelection === entry.node;
        const active = preview || this.eligible(entry, now);
        if (active) {
          if (!entry.node.isConnected) entry.anchor.before(entry.node);
        } else {
          if (entry.node.contains(document.activeElement)) restoreFocus = true;
          entry.node.remove();
        }
        for (const time of [entry.start, entry.end]) if (entry.valid && time != null && time > now) nextBoundary = Math.min(nextBoundary,time);
      }
      this.features = this.featureEntries.filter(e => e.node.isConnected).map(e => e.node);
      this.promos = this.promoEntries.filter(e => e.node.isConnected).map(e => e.node);
      this.main.hidden = !this.features.length;
      this.rail.hidden = !this.promos.length;
      this.hidden = !this.features.length && !this.promos.length && !this.editor;
      this.featureControls.hidden = this.features.length < 2;
      this.rotation.hidden = this.features.length < 2 || this.dataset.autoplay !== 'true';
      this.main.toggleAttribute('data-many', this.features.length > 4);
      this.main.dataset.features = this.features.length;
      const signature = this.features.map(slide => slide.id).join('|');
      if (this.dotSignature !== signature) this.dots.replaceChildren(...this.features.map((slide, i) => {
        const button = document.createElement('button');
        button.type = 'button'; button.className = 'vss-mh__dot'; button.dataset.featureIndex = i;
        button.setAttribute('aria-label', `Show campaign ${i + 1}: ${slide.dataset.title}`);
        button.setAttribute('aria-controls', slide.id);
        return button;
      }));
      this.dotSignature = signature;
      this.promoTrack.dataset.count = this.promos.length;
      // Maintain exactly one H1 within this hero, including when schedules change.
      this.featureEntries.forEach(entry => {
        const heading = entry.node.querySelector('.vss-mh__heading');
        const tag = this.dataset.h1 === 'true' && entry.node === this.features[0] ? 'H1' : 'H2';
        if (heading.tagName !== tag) {
          const replacement = document.createElement(tag); replacement.className = heading.className;
          replacement.append(...heading.childNodes); heading.replaceWith(replacement);
        }
      });
      const index = this.features.indexOf(old);
      this.show(index < 0 ? 0 : index, false, true);
      this.promos.forEach(node => this.hydrate(node, false));
      if (restoreFocus) {
        const target = this.features[this.current]?.querySelector('a') || this.promos[0]?.querySelector('a') || document.getElementById('main-content');
        if (target) {
          if (target.tagName === 'MAIN' && !target.hasAttribute('tabindex')) {
            target.tabIndex = -1;
            target.addEventListener('blur', () => target.removeAttribute('tabindex'), { once:true });
          }
          target.focus({ preventScroll:true });
        }
      }
      this.syncRail();
      const previewNotice = this.querySelector('[data-editor-preview]');
      if (previewNotice) previewNotice.hidden = !this.editorSelection;
      // Recheck at boundaries and at most every hour, including after sleep/clock changes.
      if (Number.isFinite(nextBoundary)) this.dateTimer = setTimeout(() => this.reconcile(), Math.min(3600000, Math.max(50,nextBoundary - now + 20)));
    }
    hydrate(node, eager = true) {
      if (!node) return;
      const img = node.querySelector('img');
      if (img && eager) img.loading = 'eager';
      node.querySelectorAll('source[data-srcset]').forEach(source => { source.srcset = source.dataset.srcset; delete source.dataset.srcset; });
      if (img?.dataset.srcset) { img.srcset = img.dataset.srcset; delete img.dataset.srcset; }
      if (img?.dataset.src) { img.src = img.dataset.src; delete img.dataset.src; }
    }
    show(index, announce = false, initial = false) {
      if (!this.features.length) { this.syncRotation(); return; }
      index = (index + this.features.length) % this.features.length;
      const outgoing = this.features[this.current];
      const hadSlideFocus = outgoing?.contains(document.activeElement);
      this.current = index;
      this.features.forEach((slide, i) => {
        const visible = i === index;
        slide.hidden = !visible; slide.inert = !visible;
        slide.setAttribute('aria-hidden', String(!visible));
        slide.setAttribute('aria-label', `${i + 1} of ${this.features.length}: ${slide.dataset.title}`);
        slide.classList.toggle('is-entering', visible && !initial && !this.motion.matches);
      });
      this.hydrate(this.features[index]);
      [...this.dots.children].forEach((dot,i) => dot.setAttribute('aria-current',String(i === index)));
      if (announce) this.status.textContent = `${index + 1} of ${this.features.length}: ${this.features[index].dataset.title}`;
      if (hadSlideFocus && outgoing !== this.features[index]) this.dots.children[index]?.focus({ preventScroll:true });
      this.syncRotation();
      clearTimeout(this.prefetchTimer);
      const first = this.features[index].querySelector('img');
      const queue = () => {
        clearTimeout(this.prefetchTimer);
        this.prefetchTimer = setTimeout(() => {
          if (!this.isConnected || this.features.length < 2 || navigator.connection?.saveData || /2g/.test(navigator.connection?.effectiveType || '')) return;
          this.hydrate(this.features[(this.current + 1) % this.features.length], true);
        }, 500);
      };
      if (first?.complete) queue();
      else if (first) this.listen(first,'load',queue,{ once:true });
    }
    click(e) {
      const feature = e.target.closest('[data-feature-step]');
      const dot = e.target.closest('[data-feature-index]');
      const promo = e.target.closest('[data-promo-step]');
      if (feature) { this.pause(); this.show(this.current + Number(feature.dataset.featureStep),true); }
      if (dot) { this.pause(); this.show(Number(dot.dataset.featureIndex),true); }
      if (promo) {
        const card = this.promos[0];
        if (card) {
          const gap = parseFloat(getComputedStyle(this.promoTrack).columnGap) || 0;
          this.promoTrack.scrollBy({ left:Number(promo.dataset.promoStep) * (card.getBoundingClientRect().width + gap), behavior:this.motion.matches ? 'instant' : 'smooth' });
        }
      }
      if (e.target.closest('[data-rotation]')) {
        this.userPaused = e.detail > 0 && this.rotationPointerState != null ? !this.rotationPointerState : !this.userPaused;
        this.rotationPointerState = null;
        this.syncRotation();
      }
    }
    pause() { this.userPaused = true; this.syncRotation(); }
    blocked() {
      return document.body.classList.contains('vss-search-locked') || !!document.querySelector('main-menu .is-open, main-menu [open], .drawer[open], .drawer.is-open');
    }
    syncRotation() {
      clearTimeout(this.rotationTimer);
      if (!this.rotation) return;
      this.rotation.textContent = this.userPaused ? 'Play rotation' : 'Pause rotation';
      const running = !this.userPaused && this.dataset.autoplay === 'true' && this.features?.length > 1 && this.inViewport && !this.hovered && !document.hidden && !this.blocked();
      this.main.dataset.rotating = String(!!running);
      if (running) this.rotationTimer = setTimeout(() => {
        if (!this.blocked() && !document.hidden) this.show(this.current + 1);
        else this.syncRotation();
      }, Math.max(6, Number(this.dataset.interval) || 8) * 1000);
    }
    syncRail() {
      if (!this.promoTrack || !this.railControls) return;
      const remaining = this.promoTrack.scrollWidth - this.promoTrack.clientWidth;
      this.railControls.hidden = remaining < 3 || !this.promos?.length;
      const offset = this.promoTrack.scrollLeft;
      this.querySelector('[data-promo-step="-1"]').disabled = offset <= 2;
      this.querySelector('[data-promo-step="1"]').disabled = offset >= remaining - 2;
    }
    revealPromo(card) {
      if (!card || !this.promoTrack) return;
      const track = this.promoTrack.getBoundingClientRect(), rect = card.getBoundingClientRect();
      if (rect.left < track.left || rect.right > track.right) this.promoTrack.scrollBy({ left:rect.left - track.left, behavior:'instant' });
    }
    editorSelect(e) {
      const blockId = e.detail?.blockId;
      const entry = [...this.featureEntries,...this.promoEntries].find(item => item.node.dataset.blockId === blockId || item.node === e.target || item.node.contains(e.target));
      if (!entry) return;
      this.pause(); this.editorSelection = entry.node; this.reconcile();
      const index = this.features.indexOf(entry.node);
      if (index >= 0) this.show(index,false,true);
      else this.revealPromo(entry.node);
    }
    disconnectedCallback() {
      this.events?.abort(); this.events = null;
      this.resize?.disconnect(); this.viewport?.disconnect(); this.overlays?.disconnect();
      clearTimeout(this.rotationTimer); clearTimeout(this.dateTimer); clearTimeout(this.prefetchTimer);
      cancelAnimationFrame(this.railFrame);
      // Preserve scheduled candidates if the editor reconnects this same element.
      for (const entry of [...(this.featureEntries || []),...(this.promoEntries || [])]) {
        if (!entry.node.parentNode) {
          const template = document.createElement('template'); template.dataset.campaignTemplate = '';
          template.content.append(entry.node); entry.anchor.before(template);
        }
        entry.anchor.remove();
      }
      delete this.dataset.ready;
    }
  }
  customElements.define('vss-merchandising-hero', VSSMerchandisingHero);
})();
